import { getApiError } from '../../utils/errorHelper';
import React, { useState } from 'react';
import { 
  Mail, Phone, Building2, ChevronRight, ChevronDown, User, Search, Edit2, Check, X, 
  Loader2, Bell, Plus, Trash2, ShoppingCart, Users as UsersIcon, FileText, ExternalLink, BookUser
} from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { adminApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { toIST } from '../../utils/dateUtils';
import ContactsPanel from './ContactsPanel';

const ORDER_STATUS_LABELS = {
  draft: 'Draft',
  invoice_requested: 'Invoice Requested',
  payment_pending: 'Payment Pending',
  payment_received: 'Payment Received',
  documents_pending: 'Documents Pending',
  under_review: 'Under Review',
  signature_pending: 'Signature Pending',
  signature_review: 'Signature Review',
  completed: 'Completed',
};

const ORDER_STATUS_COLORS = {
  draft: 'bg-slate-500/20 admin-text-dim',
  invoice_requested: 'bg-amber-500/20 text-amber-400',
  payment_pending: 'bg-yellow-500/20 text-yellow-400',
  payment_received: 'bg-emerald-500/20 text-emerald-400',
  documents_pending: 'bg-blue-500/20 text-blue-400',
  under_review: 'bg-purple-500/20 text-purple-400',
  signature_pending: 'bg-orange-500/20 text-orange-400',
  signature_review: 'bg-indigo-500/20 admin-text-green',
  completed: 'bg-emerald-500/20 text-emerald-400',
};

const UserCard = ({ user, onClick, isSelected, onExpand, isExpanded, expandedData, loadingExpanded, onSelectCompany, onSelectOrder }) => {
  const roleColors = {
    super_admin: 'bg-red-500/20 text-red-400 border-red-500/30',
    admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    client: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  return (
    <div 
      className={`admin-bg-card-inner border rounded-xl transition-all duration-300 ${
        isSelected ? 'border-[var(--admin-green)] ring-2 ' : 'border-[var(--admin-border)]'
      }`}
      data-testid={`user-card-${user.id}`}
    >
      {/* Main row */}
      <div className="p-5 cursor-pointer hover:bg-[var(--admin-bg-elevated)] rounded-t-xl" onClick={onClick}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-[var(--admin-text)] font-bold text-lg flex-shrink-0">
            {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-[var(--admin-text)] font-semibold truncate">{user.full_name || 'Unknown User'}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${roleColors[user.role] || roleColors.client}`}>
                {user.role?.replace('_', ' ') || 'Client'}
              </span>
            </div>
            <div className="flex items-center gap-2 admin-text-dim text-sm mb-2">
              <Mail className="w-4 h-4" />
              <span className="truncate">{user.email}</span>
            </div>
            {user.phone && (
              <div className="flex items-center gap-2 admin-text-soft text-sm">
                <Phone className="w-4 h-4" />
                <span>{user.phone}</span>
              </div>
            )}
          </div>
          {/* Expand button */}
          <button
            onClick={(e) => { e.stopPropagation(); onExpand(user); }}
            className="p-2 rounded-lg hover:bg-[var(--admin-bg-elevated)] transition-colors admin-text-dim hover:text-[var(--admin-text)]"
            data-testid={`expand-user-${user.id}`}
            title={isExpanded ? "Collapse" : "Expand details"}
          >
            {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        {user.companies_count > 0 && !isExpanded && (
          <div className="mt-4 pt-4 border-t border-[var(--admin-border)] flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 admin-text-soft" />
            <span className="admin-text-dim">{user.companies_count} {user.companies_count === 1 ? 'Company' : 'Companies'}</span>
          </div>
        )}
      </div>

      {/* Expanded section */}
      {isExpanded && (
        <div className="border-t border-[var(--admin-border)] admin-bg-inner" data-testid={`expanded-data-${user.id}`}>
          {loadingExpanded ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin admin-text-green" />
              <span className="ml-2 admin-text-dim text-sm">Loading details...</span>
            </div>
          ) : expandedData ? (
            <div className="p-4 space-y-4">
              {/* Summary counts */}
              <div className="grid grid-cols-4 gap-2">
                <div className="admin-field p-2.5 text-center">
                  <p className="text-lg font-bold text-[var(--admin-text)]">{expandedData.total_companies}</p>
                  <p className="text-xs admin-text-soft">Companies</p>
                </div>
                <div className="admin-field p-2.5 text-center">
                  <p className="text-lg font-bold text-[var(--admin-text)]">{expandedData.total_orders}</p>
                  <p className="text-xs admin-text-soft">Orders</p>
                </div>
                <div className="admin-field p-2.5 text-center">
                  <p className="text-lg font-bold text-[var(--admin-text)]">{expandedData.total_directors}</p>
                  <p className="text-xs admin-text-soft">Directors</p>
                </div>
                <div className="admin-field p-2.5 text-center">
                  <p className="text-lg font-bold text-[var(--admin-text)]">{expandedData.total_shareholders}</p>
                  <p className="text-xs admin-text-soft">Shareholders</p>
                </div>
              </div>

              {/* Companies list */}
              {expandedData.companies?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold admin-text-dim uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> Companies
                  </h4>
                  <div className="space-y-1.5">
                    {expandedData.companies.map((company) => (
                      <button
                        key={company.id}
                        onClick={(e) => { e.stopPropagation(); onSelectCompany(company); }}
                        className="w-full text-left admin-bg-card-inner rounded-lg p-3 hover:bg-[var(--admin-bg-elevated)] transition-colors border border-transparent hover:border-[var(--admin-green)]/30 group"
                        data-testid={`expanded-company-${company.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-[var(--admin-text)] text-sm font-medium truncate group-hover:text-[var(--admin-green)] transition-colors">
                              {company.company_name}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5 text-xs admin-text-soft">
                              <span>{company.entity_type?.replace(/_/g, ' ')}</span>
                              {company.country && <span className="px-1.5 py-0.5 rounded bg-slate-700/50">{company.country}</span>}
                              <span>{company.order_count || 0} orders</span>
                              <span>{company.directors_count || 0} directors</span>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 admin-text-soft group-hover:text-[var(--admin-green)] flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Orders list */}
              {expandedData.orders?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold admin-text-dim uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <ShoppingCart className="w-3.5 h-3.5" /> Recent Orders
                  </h4>
                  <div className="space-y-1.5">
                    {expandedData.orders.slice(0, 5).map((order) => (
                      <button
                        key={order.id}
                        onClick={(e) => { e.stopPropagation(); onSelectOrder(order); }}
                        className="w-full text-left admin-bg-card-inner rounded-lg p-3 hover:bg-[var(--admin-bg-elevated)] transition-colors border border-transparent hover:border-[var(--admin-green)]/30 group"
                        data-testid={`expanded-order-${order.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-[var(--admin-text)] text-sm font-medium truncate group-hover:text-[var(--admin-green)] transition-colors">
                              {order.product_name}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5 text-xs admin-text-soft">
                              <span>{order.company_name}</span>
                              <span>#{order.id?.slice(0, 8)}</span>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${ORDER_STATUS_COLORS[order.status] || 'bg-slate-500/20 admin-text-dim'}`}>
                            {ORDER_STATUS_LABELS[order.status] || order.status}
                          </span>
                        </div>
                      </button>
                    ))}
                    {expandedData.orders.length > 5 && (
                      <p className="text-xs admin-text-soft text-center py-1">
                        +{expandedData.orders.length - 5} more orders
                      </p>
                    )}
                  </div>
                </div>
              )}

              {expandedData.companies?.length === 0 && expandedData.orders?.length === 0 && (
                <p className="text-sm admin-text-soft text-center py-4">No companies or orders found for this user.</p>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const UserDetailPanel = ({ user, companies, onSelectCompany, onUserUpdated }) => {
  const { user: currentUser } = useAuth();
  const PLATFORM_OWNER_EMAIL = 'hi@snigdha360.com';
  const isPlatformOwner = currentUser?.email === PLATFORM_OWNER_EMAIL;
  const isOwnerTarget = user?.email === PLATFORM_OWNER_EMAIL;
  const canChangeRole = isPlatformOwner && !isOwnerTarget && (user?.role === 'admin' || user?.role === 'super_admin');
  const [changingRole, setChangingRole] = useState(false);

  const handleChangeRole = async () => {
    if (!canChangeRole) return;
    const nextRole = user.role === 'super_admin' ? 'admin' : 'super_admin';
    const verb = nextRole === 'admin' ? 'Demote' : 'Promote';
    const msg = `${verb} ${user.email} from ${user.role.replace('_', ' ')} to ${nextRole.replace('_', ' ')}?`;
    if (!window.confirm(msg)) return;
    setChangingRole(true);
    try {
      await adminApi.changeUserRole(user.id, nextRole);
      toast.success(`Role updated to ${nextRole.replace('_', ' ')}`);
      if (onUserUpdated) onUserUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change role');
    } finally {
      setChangingRole(false);
    }
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', phone: '', asana_user_gid: '' });
  const [saving, setSaving] = useState(false);
  
  // Communication preferences state
  const [showCommPrefs, setShowCommPrefs] = useState(false);
  const [commPrefs, setCommPrefs] = useState({
    send_to_user: true,
    send_to_company: false,
    send_to_directors: false,
    additional_emails: []
  });
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [recipients, setRecipients] = useState([]);

  React.useEffect(() => {
    if (user) {
      setEditForm({
        full_name: user.full_name || '',
        phone: user.phone || '',
        asana_user_gid: user.asana_user_gid || ''
      });
      loadCommPrefs();
    }
  }, [user?.id]);

  const loadCommPrefs = async () => {
    if (!user?.id) return;
    setLoadingPrefs(true);
    try {
      const prefs = await adminApi.getCommunicationPreferences(user.id);
      setCommPrefs(prefs);
      const recipientData = await adminApi.getEmailRecipients(user.id);
      setRecipients(recipientData.recipients || []);
    } catch (error) {
      console.error('Failed to load communication preferences:', error);
    } finally {
      setLoadingPrefs(false);
    }
  };

  const handleSaveCommPrefs = async () => {
    setSavingPrefs(true);
    try {
      await adminApi.updateCommunicationPreferences(user.id, commPrefs);
      toast.success('Communication preferences updated');
      const recipientData = await adminApi.getEmailRecipients(user.id);
      setRecipients(recipientData.recipients || []);
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (commPrefs.additional_emails.includes(newEmail.toLowerCase())) {
      toast.error('Email already added');
      return;
    }
    setCommPrefs(prev => ({
      ...prev,
      additional_emails: [...prev.additional_emails, newEmail.toLowerCase()]
    }));
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setCommPrefs(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.filter(e => e !== email)
    }));
  };

  const handleSave = async () => {
    if (!editForm.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateUser(user.id, {
        full_name: editForm.full_name.trim(),
        phone: editForm.phone?.trim() || null,
        asana_user_gid: editForm.asana_user_gid?.trim() || null
      });
      toast.success('User updated successfully');
      setIsEditing(false);
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="admin-card p-8 flex flex-col items-center justify-center text-center h-96">
        <User className="w-16 h-16 admin-text-soft mb-4" />
        <p className="admin-text-dim text-lg">Select a user to view details</p>
        <p className="admin-text-soft text-sm mt-1">Click on any user card to see their information</p>
      </div>
    );
  }

  return (
    <div className="admin-card overflow-hidden">
      {/* User Header */}
      <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 p-6 border-b border-[var(--admin-border)]">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-[var(--admin-text)] font-bold text-2xl">
            {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1">
            {isEditing ? (
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Full Name"
                className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] mb-2"
                autoFocus
              />
            ) : (
              <h2 className="text-xl font-bold text-[var(--admin-text)]">{user.full_name}</h2>
            )}
            <p className="admin-text-dim">{user.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs px-3 py-1 rounded-full ${
                user.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-slate-700 admin-text-dim capitalize">
                {user.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
          {!isEditing ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="border-[var(--admin-green)]/50 admin-text-green hover:bg-indigo-500/20"
            >
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-[var(--admin-text)]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({ full_name: user.full_name || '', phone: user.phone || '', asana_user_gid: user.asana_user_gid || '' });
                }}
                className="border-[var(--admin-border)] admin-text-dim"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className="p-6 space-y-4">
        <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">Contact Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="admin-field p-3">
            <p className="text-xs admin-text-soft mb-1">Email</p>
            <p className="text-[var(--admin-text)] text-sm">{user.email}</p>
          </div>
          <div className="admin-field p-3">
            <p className="text-xs admin-text-soft mb-1">Phone</p>
            {isEditing ? (
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Phone number"
                className="admin-bg-card-inner border-[var(--admin-border)] text-[var(--admin-text)] h-8 text-sm"
              />
            ) : (
              <p className="text-[var(--admin-text)] text-sm">{user.phone || 'Not provided'}</p>
            )}
          </div>
          <div className="admin-field p-3 col-span-2">
            <p className="text-xs admin-text-soft mb-1">Asana User GID</p>
            {isEditing ? (
              <Input
                value={editForm.asana_user_gid}
                onChange={(e) => setEditForm(f => ({ ...f, asana_user_gid: e.target.value }))}
                placeholder="e.g. 1214631904825797"
                className="admin-bg-card-inner border-[var(--admin-border)] text-[var(--admin-text)] h-8 text-sm font-mono"
                data-testid="asana-user-gid-input"
              />
            ) : (
              <p className="text-[var(--admin-text)] text-sm font-mono" data-testid="asana-user-gid-display">
                {user.asana_user_gid || 'Not set'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Role Management — visible to platform owner only */}
      {canChangeRole && (
        <div className="p-6 border-t border-[var(--admin-border)]" data-testid="role-mgmt-section">
          <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">Role Management</h3>
          <p className="text-xs admin-text-soft mb-3">
            Current role: <span className="text-[var(--admin-text)] font-medium">{user.role.replace('_', ' ')}</span>
          </p>
          <Button
            size="sm"
            onClick={handleChangeRole}
            disabled={changingRole}
            className={user.role === 'super_admin'
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            data-testid={user.role === 'super_admin' ? 'demote-to-admin-btn' : 'promote-to-super-admin-btn'}
          >
            {changingRole ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {user.role === 'super_admin' ? 'Demote to Admin' : 'Promote to Super Admin'}
          </Button>
          <p className="mt-2 text-[11px] admin-text-soft">
            Only the platform owner ({PLATFORM_OWNER_EMAIL}) can change roles. Audit-logged on every change.
          </p>
        </div>
      )}

      {/* Communication Preferences */}
      <div className="p-6 border-t border-[var(--admin-border)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Communication Preferences
          </h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowCommPrefs(!showCommPrefs)}
            className="admin-text-dim hover:text-[var(--admin-text)]"
          >
            {showCommPrefs ? 'Hide' : 'Configure'}
          </Button>
        </div>

        {loadingPrefs ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin admin-text-dim" />
          </div>
        ) : showCommPrefs ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commPrefs.send_to_user}
                  onChange={(e) => setCommPrefs(prev => ({ ...prev, send_to_user: e.target.checked }))}
                  className="w-4 h-4 rounded border-[var(--admin-border)] admin-bg-card-inner text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-[var(--admin-text)] text-sm">Send to User</span>
                  <p className="text-xs admin-text-soft">Send emails to {user.email}</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commPrefs.send_to_company}
                  onChange={(e) => setCommPrefs(prev => ({ ...prev, send_to_company: e.target.checked }))}
                  className="w-4 h-4 rounded border-[var(--admin-border)] admin-bg-card-inner text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-[var(--admin-text)] text-sm">Send to Company Email</span>
                  <p className="text-xs admin-text-soft">Send to associated company email addresses</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commPrefs.send_to_directors}
                  onChange={(e) => setCommPrefs(prev => ({ ...prev, send_to_directors: e.target.checked }))}
                  className="w-4 h-4 rounded border-[var(--admin-border)] admin-bg-card-inner text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-[var(--admin-text)] text-sm">Send to Directors/Founders</span>
                  <p className="text-xs admin-text-soft">Send to all associated director emails</p>
                </div>
              </label>
            </div>

            <div className="mt-4">
              <p className="text-sm admin-text-dim mb-2">Additional Email Recipients</p>
              <div className="flex gap-2 mb-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Add email address"
                  className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] text-sm flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                />
                <Button size="sm" onClick={handleAddEmail} className="admin-btn-primary ">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {commPrefs.additional_emails.length > 0 && (
                <div className="space-y-1">
                  {commPrefs.additional_emails.map((email, idx) => (
                    <div key={idx} className="flex items-center justify-between admin-bg-inner rounded px-3 py-2">
                      <span className="text-sm admin-text-dim">{email}</span>
                      <button onClick={() => handleRemoveEmail(email)} className="admin-text-soft hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={handleSaveCommPrefs}
              disabled={savingPrefs}
              className="w-full bg-emerald-600 hover:bg-emerald-700 mt-4"
            >
              {savingPrefs ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Preferences
            </Button>

            {recipients.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--admin-border)]">
                <p className="text-xs admin-text-soft uppercase mb-2">Current Recipients ({recipients.length})</p>
                <div className="flex flex-wrap gap-2">
                  {recipients.map((r, idx) => (
                    <span key={idx} className={`text-xs px-2 py-1 rounded-full ${
                      r.type === 'user' ? 'bg-blue-500/20 text-blue-400' :
                      r.type === 'company' ? 'bg-emerald-500/20 text-emerald-400' :
                      r.type === 'director' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-slate-700 admin-text-dim'
                    }`}>
                      {r.email}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm admin-text-soft">
            {recipients.length > 0 ? (
              <span>Emails sent to {recipients.length} recipient(s)</span>
            ) : (
              <span>Default: User email only</span>
            )}
          </div>
        )}
      </div>

      {/* Companies */}
      <div className="p-6 border-t border-[var(--admin-border)]">
        <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-4">Companies ({companies?.length || 0})</h3>
        {companies && companies.length > 0 ? (
          <div className="space-y-3">
            {companies.map((company) => (
              <div 
                key={company.id}
                onClick={() => onSelectCompany(company)}
                className="admin-field p-4 cursor-pointer hover:bg-[var(--admin-bg-elevated)] transition-colors border border-transparent hover:border-[var(--admin-green)]/30"
                data-testid={`company-item-${company.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[var(--admin-text)] font-medium">{company.company_name}</p>
                    <p className="admin-text-soft text-sm">{company.entity_type?.replace('_', ' ')}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 admin-text-soft" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-text-soft text-sm">No companies registered</p>
        )}
      </div>
    </div>
  );
};

export const UsersTab = ({ users, loading, onSelectUser, selectedUser, userCompanies, onSelectCompany, onUserUpdated, onSelectOrder, token }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedDataMap, setExpandedDataMap] = useState({});
  const [loadingExpandedId, setLoadingExpandedId] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('users');

  const handleExpand = async (user) => {
    if (expandedUserId === user.id) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(user.id);
    if (!expandedDataMap[user.id]) {
      setLoadingExpandedId(user.id);
      try {
        const data = await adminApi.getUserExpandedData(user.id);
        setExpandedDataMap(prev => ({ ...prev, [user.id]: data }));
      } catch (error) {
        toast.error('Failed to load user details');
      } finally {
        setLoadingExpandedId(null);
      }
    }
  };

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      {/* Sub-tabs: Users | Contacts */}
      <div className="flex items-center gap-1 mb-5 border-b border-[var(--admin-border)] pb-3">
        <button
          onClick={() => setActiveSubTab('users')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSubTab === 'users' ? 'bg-indigo-500/15 admin-text-green border border-[var(--admin-green)]/30' : 'admin-text-soft hover:text-[var(--admin-text)] hover:bg-[var(--admin-bg-elevated)]'
          }`}
          data-testid="subtab-users"
        >
          <UsersIcon className="w-4 h-4" /> Users
        </button>
        <button
          onClick={() => setActiveSubTab('contacts')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSubTab === 'contacts' ? 'bg-indigo-500/15 admin-text-green border border-[var(--admin-green)]/30' : 'admin-text-soft hover:text-[var(--admin-text)] hover:bg-[var(--admin-bg-elevated)]'
          }`}
          data-testid="subtab-contacts"
        >
          <BookUser className="w-4 h-4" /> Contacts & Filters
        </button>
      </div>

      {activeSubTab === 'contacts' ? (
        <ContactsPanel token={token} />
      ) : (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Users List */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 admin-text-soft" />
            <Input 
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 admin-bg-card-inner border-[var(--admin-border)] text-[var(--admin-text)] placeholder:admin-text-soft focus:border-[var(--admin-green)]"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="admin-card p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-700"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-700 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-slate-700 rounded w-48"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
            {filteredUsers.map((user) => (
              <UserCard 
                key={user.id} 
                user={user} 
                onClick={() => onSelectUser(user)}
                isSelected={selectedUser?.id === user.id}
                onExpand={handleExpand}
                isExpanded={expandedUserId === user.id}
                expandedData={expandedDataMap[user.id]}
                loadingExpanded={loadingExpandedId === user.id}
                onSelectCompany={onSelectCompany}
                onSelectOrder={onSelectOrder || (() => {})}
              />
            ))}
            {filteredUsers.length === 0 && (
              <div className="text-center py-8 admin-text-soft">
                No users found matching "{searchTerm}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Details */}
      <UserDetailPanel 
        user={selectedUser} 
        companies={userCompanies}
        onSelectCompany={onSelectCompany}
        onUserUpdated={onUserUpdated}
      />
    </div>
      )}
    </div>
  );
};
